import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  Network, Search, ChevronsDown, ChevronsUp, Printer, Users,
} from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import Dropdown from '../components/Dropdown';
import buildOrgTree, { countDescendants } from '../components/orgchart/orgTree';
import OrgChartTree from '../components/orgchart/OrgChartTree';
import OrgOrphansPanel from '../components/orgchart/OrgOrphansPanel';
import OrgZoomControls from '../components/orgchart/OrgZoomControls';
import useOrgChartZoom from '../components/orgchart/useOrgChartZoom';
import useCanvasPan from '../components/orgchart/useCanvasPan';
import '../components/orgchart/orgchart.css';

const EMPTY_SET = new Set();

// The page root's height, reaching exactly to the bottom of the viewport, so
// the page itself never scrolls - only the canvas inside it does. Computed
// from Layout's real chrome (client/src/components/Layout.js:309-310 - if
// that padding/header height ever changes, these three numbers need to
// change with it), not guessed: the mobile fixed header (`pt-16` = 64px,
// present below `lg`) plus twice `<main>`'s own padding at each breakpoint
// (`p-4`/`sm:p-6`/`lg:p-8`, applied symmetrically top and bottom): 64+2*16=96
// by default, 64+2*24=112 at `sm`, 0+2*32=64 at `lg` (the mobile header
// itself disappears there). `100dvh`, not `100vh` (round 4): iOS Safari's
// address-bar chrome makes `100vh` taller than the real visible viewport,
// which would push the bottom of the page below the fold and force page-
// level scroll on exactly the phones this most needs to work on.
const PAGE_ROOT_CLASS = 'space-y-6 flex flex-col '
  + 'h-[calc(100dvh-96px)] sm:h-[calc(100dvh-112px)] lg:h-[calc(100dvh-64px)]';

// Every id in the forest at depth >= 1 that has children - i.e. every id a
// progressive-disclosure toggle could hide something behind. Depth 0
// (roots) is excluded: a root's direct reports are always visible on load.
// Used both to seed the initial collapsed state and by "Collapse all",
// which returns to that same state rather than to "roots only".
const collectCollapsibleIds = (roots) => {
  const ids = new Set();
  const walk = (node, depth) => {
    if (depth >= 1 && node.children.length > 0) ids.add(node.id);
    node.children.forEach((child) => walk(child, depth + 1));
  };
  (roots || []).forEach((root) => walk(root, 0));
  return ids;
};

// Every node lying between (exclusive) the given node and a root, by walking
// `manager_id` through `byId`. Used to auto-expand a search match's
// ancestors - never the match's own children. `seen` guards a manager_id
// cycle in bad data - the server is supposed to prevent these, but a
// defensive frontend walk should never hang the tab.
const ancestorIdsOf = (node, byId) => {
  const ids = [];
  const seen = new Set();
  let cursor = node.manager_id != null ? byId[node.manager_id] : null;
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    ids.push(cursor.id);
    cursor = cursor.manager_id != null ? byId[cursor.manager_id] : null;
  }
  return ids;
};

// Shared name/position search predicate - `term` is already trimmed/lowered.
const matchesTerm = (node, term) => {
  const fullName = `${node.first_name} ${node.last_name}`.toLowerCase();
  return fullName.includes(term) || (node.position || '').toLowerCase().includes(term);
};

const OrgChartEmptyState = ({ message }) => (
  <div className="py-16 text-center text-gray-400">
    <Network className="mx-auto h-12 w-12 mb-3" />
    <p className="text-sm">{message}</p>
  </div>
);

/**
 * OrgChart - read-only company org chart built from `employees.manager_id`.
 *
 * Full mode (holders of `employees:read_all`) sees the whole company with
 * search, a department filter and expand/collapse-all. Everyone else sees
 * only their scoped spine + subtree - the server never sends peers or a
 * manager's other reports, so there is nothing here to accidentally reveal.
 * Scoped mode has no toolbar row at all (round 4): with no search/filter/
 * expand-collapse to show, "My org chart" and its subtitle fold into the
 * page header instead of leaving a near-empty bar above the canvas.
 *
 * Full mode starts with only the roots' direct reports visible (progressive
 * disclosure): every deeper node that has children starts collapsed, and a
 * toggle reveals exactly one more level at a time. Employees with no manager
 * and no reports (orphans) are grouped into a "No reporting chain" drawer
 * (`OrgOrphansPanel`, toggled by the `org-orphans-toggle` toolbar button)
 * instead of rendering as one-person trees.
 *
 * The canvas fills the rest of the page below the header/toolbar (only the
 * canvas scrolls, never the page itself); zoom controls (`OrgZoomControls`)
 * and the orphans drawer both float as overlays pinned inside it, and the
 * dotted background supports click-and-drag panning (`useCanvasPan`) in
 * addition to ctrl/cmd+wheel zoom.
 */
const OrgChart = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('full');
  const [me, setMe] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [collapsedIds, setCollapsedIds] = useState(EMPTY_SET);
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [firstMatchId, setFirstMatchId] = useState(null);
  // The "No reporting chain" drawer (Task 3): closed by default, toggled by
  // a toolbar button - see OrgOrphansPanel.js for why this replaced the old
  // always-open inline panel.
  const [orphansOpen, setOrphansOpen] = useState(false);

  // Declared before `fetchData` (which calls `resetZoom` on every fresh
  // fetch/Retry) so that reference is a plain, already-initialized binding
  // rather than one only safe because of effect-timing - see the hook for
  // what each of these does.
  const {
    zoom, setCanvasRef, treeWrapperRef, setTreeWrapperRef, sizeboxStyle, wrapperStyle,
    zoomIn, zoomOut, fit, resetZoom,
  } = useOrgChartZoom({ collapsedIds, nodes });

  // A plain ref to the same canvas DOM node `setCanvasRef` attaches its
  // wheel listener to - `useOrgChartZoom` only exposes a callback ref, not
  // the element itself, so `useCanvasPan` (a separate, independent concern)
  // gets its own ref, kept in sync via `setCanvas` below.
  const canvasElRef = useRef(null);
  const setCanvas = useCallback((el) => {
    setCanvasRef(el);
    canvasElRef.current = el;
  }, [setCanvasRef]);
  const { isDragging, ...panHandlers } = useCanvasPan(canvasElRef);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/org-chart');
      const payload = res.data.data || {};
      const nextMode = payload.mode || 'full';
      const nextNodes = payload.nodes || [];
      setMode(nextMode);
      setMe(payload.me ?? null);
      setNodes(nextNodes);
      // Full mode: seed progressive disclosure (level-1 visible, everything
      // below collapsed). Scoped mode is unchanged - the caller's subtree
      // is always fully expanded.
      if (nextMode === 'full') {
        const { roots: nextRoots } = buildOrgTree(nextNodes);
        setCollapsedIds(collectCollapsibleIds(nextRoots));
      } else {
        setCollapsedIds(EMPTY_SET);
      }
      setSearchTerm('');
      setDeptFilter('');
      setOrphansOpen(false);
      resetZoom();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load the org chart';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [resetZoom]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { roots, orphans, byId } = useMemo(() => buildOrgTree(nodes), [nodes]);
  const isFull = mode === 'full';
  // Scoped mode never shows the orphans panel: the top of the caller's own
  // spine is a root with descendants, unless the caller themselves has none
  // - in which case buildOrgTree classifies them as an "orphan", but the
  // page still renders them as a normal single root with "No direct
  // reports", exactly as before Task 6.
  const treeRoots = isFull ? roots : [...roots, ...orphans];
  const treeOrphans = isFull ? orphans : [];

  const departmentOptions = useMemo(() => {
    const seen = new Map();
    nodes.forEach((n) => {
      if (n.department_id != null && !seen.has(n.department_id)) {
        seen.set(n.department_id, n.department_name);
      }
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [nodes]);

  // Search: match name or position, case-insensitive. Expand every collapsed
  // ancestor of a match so it is actually visible, then scroll the first
  // match into view once that expansion has rendered. Never touches the
  // match's own children - only ancestors.
  useEffect(() => {
    if (!isFull) return;
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFirstMatchId(null);
      return;
    }

    const matches = nodes.filter((n) => matchesTerm(n, term));

    setFirstMatchId(matches.length > 0 ? matches[0].id : null);

    if (matches.length > 0) {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        matches.forEach((match) => {
          ancestorIdsOf(match, byId).forEach((id) => next.delete(id));
        });
        return next;
      });
    }
    // byId is derived from nodes every render; nodes is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, nodes, isFull]);

  useEffect(() => {
    if (!isFull || firstMatchId == null || !treeWrapperRef.current) return;
    const el = treeWrapperRef.current.querySelector(`[data-testid="org-node-${firstMatchId}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'center', inline: 'center' });
    }
    // treeWrapperRef comes from useOrgChartZoom's useRef() - referentially
    // stable for the component's lifetime - but ESLint can't see through a
    // custom hook to know that, so it is listed explicitly rather than
    // suppressed.
  }, [firstMatchId, collapsedIds, isFull, treeWrapperRef]);

  const matchIds = useMemo(() => {
    if (!isFull) return EMPTY_SET;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return EMPTY_SET;
    const ids = nodes.filter((n) => matchesTerm(n, term)).map((n) => n.id);
    return new Set(ids);
  }, [nodes, searchTerm, isFull]);

  const dimmedIds = useMemo(() => {
    if (!isFull || !deptFilter) return EMPTY_SET;
    const ids = nodes
      .filter((n) => String(n.department_id) !== String(deptFilter))
      .map((n) => n.id);
    return new Set(ids);
  }, [nodes, deptFilter, isFull]);

  const handleToggle = useCallback((id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExpandAll = () => setCollapsedIds(EMPTY_SET);
  const handleCollapseAll = () => setCollapsedIds(collectCollapsibleIds(roots));

  const meNode = me != null ? byId[me] : null;
  const scopedReportCount = meNode ? countDescendants(meNode) : 0;
  // A scoped IC with zero direct reports gets a distinct title/subtitle
  // instead of the numeric "0 people report to you", which reads like an
  // error rather than the ordinary case it is - the chart still renders
  // their own spine underneath exactly as before.
  const scopedHasNoReports = !isFull && scopedReportCount === 0;
  const title = isFull ? 'Org chart' : (scopedHasNoReports ? 'Your reporting line' : 'My org chart');
  const subtitle = isFull
    ? `${nodes.length} active employee${nodes.length === 1 ? '' : 's'}`
    : (scopedHasNoReports
      ? "You don't have any direct reports."
      : `${scopedReportCount} people report to you`);

  if (loading) {
    return (
      <div data-testid="org-chart-page" className={PAGE_ROOT_CLASS}>
        <div className="flex items-center space-x-3 shrink-0">
          <Network className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Org chart</h1>
        </div>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="org-chart-page" className={PAGE_ROOT_CLASS}>
        <div className="flex items-center space-x-3 shrink-0">
          <Network className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Org chart</h1>
        </div>
        <div className="bg-white rounded-lg shadow-soft p-12 text-center">
          <p className="text-danger-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasNoReportingTree = nodes.length > 0 && roots.length === 0 && orphans.length === 0;

  return (
    <div data-testid="org-chart-page" className={PAGE_ROOT_CLASS}>
      <div className="flex items-center space-x-3 shrink-0">
        <Network className="h-7 w-7 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {title}
          </h1>
          <p className="text-gray-600">
            {isFull ? 'Reporting lines from the employee directory.' : subtitle}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft overflow-hidden flex flex-col flex-1 min-h-0">
        {isFull && (
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 shrink-0">
            <div className="mr-auto">
              <h2 className="text-base font-bold text-gray-900">Company org chart</h2>
              <p className="text-xs text-gray-500">{subtitle}</p>
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                data-testid="org-search"
                aria-label="Search people"
                placeholder="Find a person..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-56 rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm
                  focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            <div className="w-52">
              <Dropdown
                aria-label="Department"
                data-testid="org-dept-filter"
                value={deptFilter}
                onChange={(event) => setDeptFilter(event.target.value)}
              >
                <option value="">All departments</option>
                {departmentOptions.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </Dropdown>
            </div>

            <button
              type="button"
              data-testid="org-expand-all"
              onClick={handleExpandAll}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2
                text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ChevronsDown size={15} /> Expand all
            </button>
            <button
              type="button"
              data-testid="org-collapse-all"
              onClick={handleCollapseAll}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2
                text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ChevronsUp size={15} /> Collapse all
            </button>

            {treeOrphans.length > 0 && (
              // Opens the "No reporting chain" drawer (Task 3) - a plain
              // toggle button rather than an always-open inline panel, so
              // the panel itself (OrgOrphansPanel, rendered in the canvas
              // shell below) can never be clipped by the tree's own
              // horizontal scroll.
              <button
                type="button"
                data-testid="org-orphans-toggle"
                aria-expanded={orphansOpen}
                onClick={() => setOrphansOpen((open) => !open)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2
                  text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Users size={15} /> No reporting chain &middot; {treeOrphans.length}
              </button>
            )}

            {/* Plain <a target="_blank">, not client-side navigation: the
                export document is company-wide by definition (full mode
                only, hence living inside this `isFull` toolbar), and opening
                it in a new tab keeps the interactive chart's search/filter/
                collapse state intact behind it. Last control in the row. */}
            <a
              href="/org-chart/export"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="org-export"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2
                text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Printer size={15} /> Export PDF
            </a>
          </div>
        )}

        {nodes.length === 0 ? (
          <div className="relative flex flex-1 min-h-0 items-center justify-center">
            <OrgZoomControls zoom={zoom} zoomIn={zoomIn} zoomOut={zoomOut} fit={fit} />
            <OrgChartEmptyState message="No employees found." />
          </div>
        ) : hasNoReportingTree ? (
          // Defensive case of a manager_id cycle with no true root or orphan
          // (nodes.length > 0 but every node has a manager also present in
          // the payload) - nothing renderable, but distinct from "no
          // employees" since the subtitle above still shows the count.
          <div className="relative flex flex-1 min-h-0 items-center justify-center">
            <OrgZoomControls zoom={zoom} zoomIn={zoomIn} zoomOut={zoomOut} fit={fit} />
            <OrgChartEmptyState message="No reporting tree could be built." />
          </div>
        ) : (
          <div className="relative flex-1 min-h-0">
            {/* Rendered BEFORE the canvas (round 4, IMPORTANT 3): absolute
                positioning makes it visually identical either way, but tab
                order follows DOM order - with the canvas first, a keyboard
                user had to tab through every card/toggle/link in the tree
                (hundreds, on a large company) before ever reaching Zoom
                out/Zoom in/Fit. */}
            <OrgZoomControls zoom={zoom} zoomIn={zoomIn} zoomOut={zoomOut} fit={fit} />
            <OrgOrphansPanel
              orphans={treeOrphans}
              open={orphansOpen}
              onClose={() => setOrphansOpen(false)}
              me={me}
              highlightedIds={matchIds}
              dimmedIds={dimmedIds}
            />

            <div
              data-testid="org-chart-canvas"
              ref={setCanvas}
              onPointerDown={panHandlers.onPointerDown}
              onPointerMove={panHandlers.onPointerMove}
              onPointerUp={panHandlers.onPointerUp}
              onPointerCancel={panHandlers.onPointerCancel}
              onPointerLeave={panHandlers.onPointerLeave}
              onLostPointerCapture={panHandlers.onLostPointerCapture}
              className={twMerge(
                'org-chart-canvas h-full overflow-auto',
                // `select-none` while dragging (round 4, IMPORTANT 1) - a
                // mousedown+move over a card's text is exactly the native
                // text-selection gesture, and panning across several cards
                // would otherwise highlight their names/positions along the
                // way. Belt-and-suspenders with `useCanvasPan`'s own
                // `preventDefault()` on the starting pointerdown.
                isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
              )}
            >
              <div className="org-chart-zoom-sizebox" style={sizeboxStyle}>
                <div
                  ref={setTreeWrapperRef}
                  data-testid="org-chart-zoom-wrapper"
                  className="org-chart-zoom-wrapper"
                  style={wrapperStyle}
                >
                  <OrgChartTree
                    roots={treeRoots}
                    mode={mode}
                    me={me}
                    collapsedIds={collapsedIds}
                    onToggle={handleToggle}
                    highlightedIds={matchIds}
                    dimmedIds={dimmedIds}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgChart;
